import type {
    DockFlyoutFiltersState,
    DockFlyoutRowAction,
    DockFlyoutRowActionId,
    DockFlyoutScope,
} from "../../types/dockFlyoutDataTypes";

export interface ItemSortConfig<T> {
    compareByField?: (sortId: string) => (a: T, b: T) => number;
    defaultComparator?: (a: T, b: T) => number;
}

export interface ItemQueryConfig<T> {
    searchableFields: (item: T) => string[];
}

export interface ItemScopeConfig<T> {
    getScopeValue: (item: T) => string | undefined;
}

export interface ItemFilterConfig<T> {
    matchesFilter: (item: T, filters: DockFlyoutFiltersState) => boolean;
}

const ACTION_LABELS: Record<DockFlyoutRowActionId, string> = {
    pin: "Pin",
    share: "Share",
    edit: "Edit",
    rename: "Rename",
    copy: "Copy",
    delete: "Delete",
    download: "Download",
};

export function mapActionIdsToActions(
    actionIds: DockFlyoutRowActionId[],
): DockFlyoutRowAction[] {
    return actionIds.map((id) => ({
        id,
        label: ACTION_LABELS[id],
    }));
}

export function normalizeValue(value?: string): string {
    return value?.trim().toLowerCase() ?? "";
}

export function paginateItems<T>(
    items: T[],
    page: number,
    pageSize: number,
): {
    items: T[];
    total: number;
    hasMore: boolean;
} {
    const safePage = Math.max(page, 1);
    const safePageSize = Math.max(pageSize, 1);
    const startIndex = (safePage - 1) * safePageSize;
    const endIndex = startIndex + safePageSize;

    return {
        items: items.slice(startIndex, endIndex),
        total: items.length,
        hasMore: endIndex < items.length,
    };
}

export function sortItems<T>(
    items: T[],
    sortId: string | undefined,
    config: ItemSortConfig<T>,
): T[] {
    const sorted = [...items];

    if (config.compareByField && sortId) {
        return sorted.sort(config.compareByField(sortId));
    }

    if (config.defaultComparator) {
        return sorted.sort(config.defaultComparator);
    }

    return sorted;
}

export function matchesQuery<T>(
    item: T,
    query: string | undefined,
    config: ItemQueryConfig<T>,
): boolean {
    const normalizedQuery = normalizeValue(query);

    if (!normalizedQuery) {
        return true;
    }

    return config.searchableFields(item).some((field) =>
        field.toLowerCase().includes(normalizedQuery),
    );
}

export function matchesScope<T>(
    item: T,
    scope: string | undefined,
    config: ItemScopeConfig<T>,
): boolean {
    if (!scope || scope === "all") {
        return true;
    }

    return config.getScopeValue(item) === scope;
}

export function matchesFilters<T>(
    item: T,
    filters: DockFlyoutFiltersState | undefined,
    config: ItemFilterConfig<T>,
): boolean {
    if (!filters) {
        return true;
    }

    return config.matchesFilter(item, filters);
}

export function buildOwnershipScopes(
    ownershipOptions: Array<{ key: string; label: string }>,
    allLabel: string,
): DockFlyoutScope[] {
    return [
        { id: "all", label: allLabel },
        ...ownershipOptions.map((option) => ({ id: option.key, label: option.label })),
    ];
}

export function mockDelay<T>(data: T, ms = 0): Promise<T> {
    if (ms === 0) {
        return Promise.resolve(data);
    }

    return new Promise((resolve) => setTimeout(() => resolve(data), ms));
}